/**
 * Import Monitor — Rules & mappings.
 *
 * How a tenant's feed is interpreted: strategy + handler business rules,
 * the feed-key alias table each import service accepts, profile toggles
 * and performance settings, queue wiring, and the raw keys the latest
 * pushes carried. Self-described by the backend code that applies it.
 */
import api from './client';

export interface ImportRuleItem {
  /** closing | grn_linking | mapping | handler */
  source: string;
  area: string;
  rule: string;
  detail: string;
}

export interface ImportServiceAliases {
  data_type: string;
  service: string;
  module: string;
  aliases: { source: string; canonical: string }[];
}

export interface ImportQueueWiring {
  data_type: string;
  redis_key: string;
  schedule: string;
  is_incremental: boolean;
  is_active: boolean;
  last_success_at: string | null;
  field_mappings: Record<string, unknown>;
}

export interface ImportObservedFeed {
  data_type: string;
  job_id: number;
  at: string;
  total_records: number;
  records_created: number;
  records_updated: number;
  records_failed: number;
  raw_keys: string[];
  processed_keys: string[];
  /** pipeline envelope (_source_key, _row_number…) — not feed fields */
  envelope_keys: string[];
  /** raw keys no alias or canonical name covers — nobody reads these */
  unmapped_keys: string[];
  sample_raw: Record<string, unknown>[];
  sample_processed: Record<string, unknown>[];
}

export interface ImportRulesCatalogue {
  customer: { id: number; name: string; base_currency: string };
  handler: {
    name: string;
    class: string;
    strategies: { closing: string; grn_linking: string; mapping: string };
  };
  rules: ImportRuleItem[];
  services: ImportServiceAliases[];
  profile: Record<string, unknown>;
  settings: Record<string, unknown>;
  queues: ImportQueueWiring[];
  observed: ImportObservedFeed[];
}

export const importRulesAPI = {
  /** Rules, mappings and settings the import service applies to one customer's feed. */
  getRules: (customerId: number) =>
    api.get<ImportRulesCatalogue>('/imports/rules/', { params: { customer_id: customerId } })
      .then(r => r.data),
};
